import { Card } from "../ui/Card";
import type { JSX } from "react";

interface SubmissionResultProps {
  category: string;
  labId: string;
  calculatedResults?: ResultData;
  // baseline or savings mode controls the title label
  mode?: "baseline" | "savings";
}

interface FieldValue {
  field: JSX.Element;
  value: JSX.Element;
}

interface ResultData {
  cost?: number;
  emissions?: number;
  energy?: number;
  waste?: number;
  storage?: number;
  [key: string]: number | undefined; // Allow other numeric keys
}

// Define column configurations for each category
const CATEGORY_COLUMNS: Record<string, Record<string, FieldValue>> = {
  "Computer Hardware": {
    energy: {
      field: (
        <span>
          <strong>Energy</strong> (kWh)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    cost: {
      field: (
        <span>
          <strong>Cost</strong> (AUD)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
  "Eco Office": {
    waste: {
      field: (
        <span>
          <strong>Waste</strong> (kg)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    cost: {
      field: (
        <span>
          <strong>Cost</strong> (AUD)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
  "Data Storage": {
    energy: {
      field: (
        <span>
          <strong>Energy</strong> (kWh)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    cost: {
      field: (
        <span>
          <strong>Cost</strong> (AUD)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    storage: {
      field: (
        <span>
          <strong>Storage</strong> (TB)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
  "Cold Storage": {
    energy: {
      field: (
        <span>
          <strong>Energy</strong> (kWh)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    cost: {
      field: (
        <span>
          <strong>Cost</strong> (AUD)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
  "Lab Waste": {
    waste: {
      field: (
        <span>
          <strong>Waste</strong> (kg)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    cost: {
      field: (
        <span>
          <strong>Cost</strong> (AUD)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
  "Transport": {
    energy_mj: {
      field: (
        <span>
          <strong>Energy</strong> (MJ)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions_kgco2e: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
  "Procurement": {
    units: {
      field: (
        <span>
          <strong>Units Procured</strong>
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    waste: {
      field: (
        <span>
          <strong>Waste</strong> (kg)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    cost: {
      field: (
        <span>
          <strong>Cost</strong> (AUD)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
  "Green Chemistry": {
    volume_ml: {
      field: (
        <span>
          <strong>Volume</strong> (mL)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    cost: {
      field: (
        <span>
          <strong>Cost</strong> (AUD)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    emissions: {
      field: (
        <span>
          <strong>Emissions</strong> (kgCO2e)
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
    toxicity_burden: {
      field: (
        <span>
          <strong>Toxicity Burden</strong>
        </span>
      ),
      value: <div className="skeleton h-4 w-full"></div>,
    },
  },
};

/**
 * Once a baseline calculator has been submitted, this result component is shown
 * @constructor
 */
export const SubmissionResult = ({
  category,
  calculatedResults,
  mode = "baseline",
}: SubmissionResultProps) => {
  // Get the column configuration for the current category
  const categoryColumns = CATEGORY_COLUMNS[category];

  if (!categoryColumns) {
    console.warn(`Unknown category: ${category}`);
    return (
      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">
          {mode === "baseline"
            ? "Projected Annual Cost"
            : "Projected Annual Savings"}
        </h2>
        <p className="text-gray-600">
          No data available for category: {category}
        </p>
      </Card>
    );
  }

  const getFormattedValue = (key: string): JSX.Element => {
    const value = calculatedResults ? calculatedResults[key] : undefined;

    if (value === undefined || value === null) {
      return <span className="text-gray-400 font-normal">N/A</span>;
    }

    if (key === "cost") {
      return <span>${value.toLocaleString()}</span>;
    } else if (key === "emissions") {
      return <span>{value.toLocaleString()} kgCO2e</span>;
    } else if (key === "energy") {
      return <span>{value.toLocaleString()} kWh</span>;
    } else if (key === "waste") {
      return <span>{value.toLocaleString()} kg</span>;
    } else if (key === "units") {
      return <span>{value.toLocaleString()}</span>;
    } else if (key === "storage") {
      return <span>{value.toLocaleString()} TB</span>;
    } else if (key === "energy_mj") {
      return <span>{value.toLocaleString()} MJ</span>;
    } else if (key === "emissions_kgco2e") {
      return <span>{value.toLocaleString()} kgCO2e</span>;
    } else if (key === "volume_ml") {
      return <span>{value.toLocaleString()} mL</span>;
    } else if (key === "toxicity_burden") {
      return <span>{value.toLocaleString()}</span>;
    } else {
      return <span>{value}</span>;
    }
  };

  return (
    <Card className="p-6">
      <h2 className="text-xl font-bold mb-4">
        {mode === "baseline"
          ? "Projected Annual Cost"
          : "Projected Annual Savings"}
      </h2>
      <div className="overflow-x-auto">
        <div className="card-content">
          <table className="table-auto">
            <tbody>
              <tr>
                {Object.entries(categoryColumns).map(
                  ([key, fieldValue], index, arr) => (
                    <td
                      key={key}
                      className={`${index === 0 ? "pr-4" : index === arr.length - 1 ? "pl-4" : "px-4"} py-2 font-medium`}
                    >
                      {fieldValue.field}
                    </td>
                  ),
                )}
              </tr>
              <tr>
                {Object.keys(categoryColumns).map((key, index, arr) => (
                  <td
                    key={"value-" + key}
                    className={`${index === 0 ? "pr-4" : index === arr.length - 1 ? "pl-4" : "px-4"} py-2 text-right`}
                  >
                    {getFormattedValue(key)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
};
